import { FormValues } from '../components/form/types';
import { UploadedFileResponse } from './types/cms';
import { UserLogFilter } from './types/user';
import { DashboardResponse } from './types';
import { ApiFactoryClassType } from './factory';

const user = (factory: ApiFactoryClassType) => ({
    getDashboardData: async (
        signal?: AbortSignal,
    ): Promise<DashboardResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL('/api/user/dashboard', factory.getApiHost()),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                    signal,
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            if (e !== 'abort') {
                factory.onNetworkError();
            }
            return null;
        }
    },
    getOwnData: async (signal?: AbortSignal) => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL('/api/user/me', factory.getApiHost()),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                    signal,
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            if (e !== 'abort') {
                factory.onNetworkError();
            }
            return null;
        }
    },
    updateImage: async (
        file: File,
        signal?: AbortSignal,
    ): Promise<UploadedFileResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const data = new FormData();
            data.append('file', file);

            const response = await fetch(
                new URL('/api/user/me/update-image', factory.getApiHost()),
                {
                    method: 'POST',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    body: data,
                    mode: 'cors',
                    signal,
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            if (e !== 'abort') {
                factory.onNetworkError();
            }
            return null;
        }
    },
    changePassword: async (passwordObj: FormValues, signal?: AbortSignal) => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL('/api/user/me/change-password', factory.getApiHost()),
                {
                    method: 'POST',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(passwordObj),
                    mode: 'cors',
                    signal,
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            if (e !== 'abort') {
                factory.onNetworkError();
            }
            return null;
        }
    },
    updateStatus: async (status: string, signal?: AbortSignal) => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL('/api/user/me/status-update', factory.getApiHost()),
                {
                    method: 'POST',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ status }),
                    mode: 'cors',
                    signal,
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            if (e !== 'abort') {
                factory.onNetworkError();
            }
            return null;
        }
    },
    getLogs: async (
        limit: number = 15,
        offset: number = 0,
        filter: UserLogFilter = {},
        signal?: AbortSignal,
    ) => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const queryFilter = new URLSearchParams(filter).toString();

            const response = await fetch(
                new URL(
                    `/api/user/me/log?limit=${limit}&offset=${offset}${queryFilter ? `&${queryFilter}` : ``}`,
                    factory.getApiHost(),
                ),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                    signal,
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            if (e !== 'abort') {
                factory.onNetworkError();
            }
            return null;
        }
    },
});

export default user;
